import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface CommunityProfile {
  user_id: string;
  handle: string;
  display_name: string | null;
  bio: string | null;
  avatar_url: string | null;
}

async function ensureProfile(userId: string, fallbackName: string): Promise<CommunityProfile> {
  const { data: existing } = await supabase
    .from('community_profiles')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (existing) return existing as CommunityProfile;

  // generate handle from fallback name + suffix
  const base = (fallbackName || 'member')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 16) || 'member';
  let handle = base;
  for (let i = 0; i < 5; i++) {
    const { data: clash } = await supabase
      .from('community_profiles')
      .select('user_id')
      .eq('handle', handle)
      .maybeSingle();
    if (!clash) break;
    handle = `${base}${Math.floor(Math.random() * 9999)}`;
  }

  const { data, error } = await supabase
    .from('community_profiles')
    .insert({ user_id: userId, handle, display_name: fallbackName })
    .select()
    .single();
  if (error) throw error;
  return data as CommunityProfile;
}

export function useCommunityProfile() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['community-profile', user?.id],
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const meta = (user!.user_metadata as any) || {};
      const fallback =
        meta.first_name || meta.full_name?.split(' ')[0] || user!.email?.split('@')[0] || 'member';
      return ensureProfile(user!.id, fallback);
    },
  });
}
